/**
 * Utility functions for date formatting and conversion
 */

/**
 * Convert a date string (YYYY-MM-DD) to end of day datetime (YYYY-MM-DDTHH:MM:SS)
 * Used for due dates to set them to end of day
 */
export const toEndOfDayDateTime = (dateString: string): string => {
  return `${dateString}T23:59:59`;
};

/**
 * Extract date part from datetime string (YYYY-MM-DDTHH:MM:SS -> YYYY-MM-DD)
 * Used for displaying dates in form inputs
 */
export const extractDatePart = (dateTimeString: string): string => {
  return dateTimeString.split('T')[0];
};

/**
 * Format a date string for display
 */
export const formatDateForDisplay = (dateString?: string): string | null => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString();
};

/**
 * Check if a date is today or earlier
 */
export const isDateTodayOrEarlier = (dateString: string): boolean => {
  if (!dateString) return false;
  
  const today = new Date();
  const todayDateString = today.toISOString().split('T')[0]; // Get YYYY-MM-DD format
  
  // Extract date part from the input (handles both YYYY-MM-DD and YYYY-MM-DDTHH:MM:SS formats)
  const inputDateString = dateString.split('T')[0];
  
  // Compare date strings directly to avoid timezone issues
  return inputDateString <= todayDateString;
};

/**
 * Parse a UTC timestamp from the API and return a Date object.
 * Use this for system timestamps (created_time, completed_date, etc.)
 * which are stored in UTC but may lack a 'Z' suffix.
 */
export const parseUTCTimestamp = (timestamp: string): Date => {
  // If timestamp doesn't have timezone info, treat it as UTC
  if (timestamp && !timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.includes('-', timestamp.length - 6)) {
    return new Date(timestamp + 'Z');
  }
  return new Date(timestamp);
};

/**
 * Parse a local wall-clock timestamp (no timezone conversion).
 * Use this for event start_time/end_time which represent local time.
 */
export const parseLocalTimestamp = (timestamp: string): Date => {
  return new Date(timestamp);
}; 